import {
  Gauge,
  ShieldCheck,
  Flame,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const BG =
  "https://images.pexels.com/photos/9702354/pexels-photo-9702354.jpeg";

const cards = [
  {
    icon: Gauge,
    tag: "Performance",
    title: "Stage 1",
    desc:
      "Reprogrammation du calculateur moteur d'origine pour des gains de puissance et de couple mesurables, sans modification matérielle.",
    points: [
      "+15% à +30% de puissance selon moteur",
      "Couple à bas régime amélioré",
      "Consommation optimisée à charge partielle",
    ],
    accent: "from-blue-500/20 to-blue-500/0",
    iconBg: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    serviceType: "Reprogrammation Stage 1",
  },
  {
    icon: ShieldCheck,
    tag: "Fiabilité",
    title: "Suppression FAP / EGR / AdBlue",
    desc:
      "Solutions durables pour les véhicules sujets aux défaillances récurrentes de dépollution. Intervention logicielle propre et documentée.",
    points: [
      "Suppression FAP / EGR / AdBlue",
      "Fiabilité moteur renforcée",
      "Frais de maintenance réduits",
    ],
    accent: "from-cyan-500/20 to-cyan-500/0",
    iconBg: "bg-cyan-500/10 text-cyan-300 border-cyan-500/20",
    serviceType: "Suppression FAP / EGR / AdBlue",
  },
  {
    icon: Flame,
    tag: "Économie",
    title: "E85 / Flex Fuel",
    desc:
      "Conversion à l'éthanol E85 avec reprogrammation adaptée. Économies importantes à la pompe et réduction des émissions de CO₂.",
    points: [
      "Compatibilité essence + E85",
      "Jusqu'à -40% sur le carburant",
      "Déclaration conforme à la réglementation",
    ],
    accent: "from-amber-500/20 to-amber-500/0",
    iconBg: "bg-amber-500/10 text-amber-300 border-amber-500/20",
    serviceType: "E85 / Flex Fuel",
  },
];

export default function Reprogrammation({ onQuoteClick }) {
  return (
    <section
      id="reprogrammation"
      data-testid="reprogrammation-section"
      className="relative py-24 lg:py-32 bg-slate-950 text-white overflow-hidden grain"
    >
      <img
        src={BG}
        alt="Banc de reprogrammation moteur"
        className="absolute inset-0 w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/90 to-slate-950" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">
                Nouveau
              </span>
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter">
              Reprogrammation <br />
              <span className="text-blue-400">moteur.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-xl text-[15px] leading-relaxed">
              Gagnez en puissance, en fiabilité ou en économies de carburant.
              Nos prestations de cartographie moteur sont réalisées avec des
              fichiers sur-mesure et un banc de puissance adapté à votre véhicule.
            </p>
          </div>
          <button
            data-testid="reprog-cta-quote"
            onClick={() => onQuoteClick?.("Reprogrammation moteur")}
            className="lift self-start group inline-flex items-center gap-2 rounded-md bg-blue-500 hover:bg-blue-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-blue-500/20"
          >
            Devis reprogrammation
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <article
              key={c.title}
              data-testid={`reprog-card-${i}`}
              className="group relative rounded-lg border border-slate-800 bg-slate-900/50 backdrop-blur p-7 lift hover:border-blue-500/40 hover:bg-slate-900"
            >
              <div
                className={`absolute inset-0 -z-10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br ${c.accent}`}
              />
              <div className="flex items-center justify-between mb-5">
                <div
                  className={`h-11 w-11 grid place-items-center rounded-md border ${c.iconBg}`}
                >
                  <c.icon size={20} strokeWidth={2} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  {c.tag}
                </span>
              </div>
              <h3 className="font-display font-black text-2xl text-white mb-3">
                {c.title}
              </h3>
              <p className="text-slate-400 text-[14.5px] leading-relaxed mb-5">
                {c.desc}
              </p>
              <ul className="space-y-2 mb-6">
                {c.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-sm text-slate-300"
                  >
                    <CheckCircle2
                      size={16}
                      className="text-blue-400 mt-0.5 shrink-0"
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`reprog-card-cta-${i}`}
                onClick={() => onQuoteClick?.(c.serviceType)}
                className="text-sm font-semibold text-blue-300 hover:text-blue-200 inline-flex items-center gap-1.5 group/btn"
              >
                Demander un devis
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover/btn:translate-x-1"
                />
              </button>
            </article>
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-500 max-w-3xl">
          Note : La reprogrammation et les suppressions de dispositifs de
          dépollution peuvent avoir un impact sur l'homologation du véhicule
          pour une utilisation sur voie publique. Nos techniciens vous
          conseillent et documentent chaque intervention.
        </p>
      </div>
    </section>
  );
}
